import { cn } from "@/lib/utils"

export function TruckLoader({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "min-h-screen flex flex-col items-center justify-center gap-5 bg-background",
        className
      )}
    >
      {/* Truck SVG — extra top viewBox headroom for exhaust smoke */}
      <div className="w-72 animate-[lst-truck-bounce_0.55s_ease-in-out_infinite]">
        <svg
          viewBox="0 -12 200 92"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* ── Exhaust smoke ── */}
          <circle cx="130" cy="-2" r="4" fill="#94a3b8">
            <animate attributeName="cy"      values="-2;-10;-2"      dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.55;0;0.55"    dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="r"       values="4;7;4"          dur="1.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="125" cy="-6" r="2.5" fill="#94a3b8">
            <animate attributeName="cy"      values="-6;-14;-6"      dur="1.4s" begin="0.45s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0;0.4"      dur="1.4s" begin="0.45s" repeatCount="indefinite" />
          </circle>
          <circle cx="133" cy="-4" r="2" fill="#94a3b8">
            <animate attributeName="cy"      values="-4;-11;-4"      dur="1.4s" begin="0.9s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0;0.3"      dur="1.4s" begin="0.9s" repeatCount="indefinite" />
          </circle>

          {/* ── Exhaust pipe (behind cab top) ── */}
          <rect x="127" y="4" width="5" height="10" rx="1.5" fill="#3730a3" />

          {/* ── Cargo trailer ── */}
          {/* Main body */}
          <rect x="2" y="14" width="122" height="50" rx="2" fill="#6366f1" />
          {/* Top highlight stripe */}
          <rect x="2" y="14" width="122" height="5" rx="2" fill="#818cf8" />
          {/* Horizontal panel ribs */}
          <line x1="2"   y1="32" x2="124" y2="32" stroke="#818cf8" strokeWidth="0.9" />
          <line x1="2"   y1="50" x2="124" y2="50" stroke="#818cf8" strokeWidth="0.9" />
          {/* Vertical panel dividers */}
          <line x1="34"  y1="14" x2="34"  y2="64" stroke="#818cf8" strokeWidth="0.8" />
          <line x1="66"  y1="14" x2="66"  y2="64" stroke="#818cf8" strokeWidth="0.8" />
          <line x1="98"  y1="14" x2="98"  y2="64" stroke="#818cf8" strokeWidth="0.8" />
          {/* Rear door */}
          <rect x="2" y="14" width="6" height="50" rx="1" fill="#4f46e5" />
          {/* Door handle */}
          <rect x="6" y="37" width="3" height="6" rx="1" fill="#3730a3" />
          {/* Bottom chassis rail */}
          <rect x="0" y="62" width="127" height="5" rx="1" fill="#4338ca" />

          {/* ── Cab / tractor ── */}
          {/* Cab body (with angled front profile) */}
          <path
            d="M124,8 L124,64 L188,64 L188,26 L171,8 Z"
            fill="#4f46e5"
          />
          {/* Cab roof panel */}
          <rect x="124" y="6" width="47" height="4" rx="2" fill="#4338ca" />
          {/* Sleeper box detail at rear of cab */}
          <rect x="124" y="10" width="26" height="4" rx="1" fill="#4338ca" opacity="0.6" />
          {/* Windshield (angled, semi-transparent) */}
          <path
            d="M152,10 L169,10 L186,27 L186,48 L152,48 Z"
            fill="#bfdbfe"
            opacity="0.4"
          />
          <path
            d="M152,10 L169,10 L186,27 L186,48 L152,48 Z"
            stroke="#3730a3"
            strokeWidth="1.2"
            fill="none"
          />
          {/* Driver side window */}
          <rect x="126" y="12" width="24" height="24" rx="1" fill="#bfdbfe" opacity="0.3" />
          <rect x="126" y="12" width="24" height="24" rx="1" stroke="#3730a3" strokeWidth="0.9" fill="none" />
          {/* Cab door divider line */}
          <line x1="152" y1="10" x2="152" y2="64" stroke="#3730a3" strokeWidth="1.2" />
          {/* Door handle */}
          <rect x="158" y="55" width="6" height="2.5" rx="1" fill="#3730a3" />
          {/* Headlight */}
          <rect x="185" y="38" width="4" height="10" rx="1.5" fill="#fef08a" />
          <rect x="185.5" y="38.5" width="3" height="9" rx="1" fill="#fefce8" opacity="0.85" />
          {/* Front bumper */}
          <rect x="184" y="55" width="6" height="9" rx="2" fill="#3730a3" />
          {/* Side mirror */}
          <rect x="186" y="22" width="7" height="5" rx="1.5" fill="#4338ca" />
          {/* Fuel tank (side of cab) */}
          <rect x="125" y="46" width="6" height="16" rx="1" fill="#4338ca" />
          {/* Cab bottom step */}
          <rect x="159" y="62" width="20" height="3" rx="1" fill="#3730a3" />

          {/* ── Rear dual wheels ── */}
          <g>
            <animateTransform attributeName="transform" type="rotate"
              from="0 28 68" to="360 28 68" dur="0.9s" repeatCount="indefinite" />
            <circle cx="28" cy="68" r="12" fill="#1e293b" />
            <circle cx="28" cy="68" r="8"  fill="#334155" />
            <circle cx="28" cy="68" r="3"  fill="#64748b" />
            <line x1="28" y1="59" x2="28" y2="77" stroke="#475569" strokeWidth="1.3" />
            <line x1="19" y1="68" x2="37" y2="68" stroke="#475569" strokeWidth="1.3" />
            <line x1="21.6" y1="61.6" x2="34.4" y2="74.4" stroke="#475569" strokeWidth="1" />
            <line x1="34.4" y1="61.6" x2="21.6" y2="74.4" stroke="#475569" strokeWidth="1" />
          </g>
          <g>
            <animateTransform attributeName="transform" type="rotate"
              from="0 52 68" to="360 52 68" dur="0.9s" repeatCount="indefinite" />
            <circle cx="52" cy="68" r="12" fill="#1e293b" />
            <circle cx="52" cy="68" r="8"  fill="#334155" />
            <circle cx="52" cy="68" r="3"  fill="#64748b" />
            <line x1="52" y1="59" x2="52" y2="77" stroke="#475569" strokeWidth="1.3" />
            <line x1="43" y1="68" x2="61" y2="68" stroke="#475569" strokeWidth="1.3" />
            <line x1="45.6" y1="61.6" x2="58.4" y2="74.4" stroke="#475569" strokeWidth="1" />
            <line x1="58.4" y1="61.6" x2="45.6" y2="74.4" stroke="#475569" strokeWidth="1" />
          </g>
          {/* Rear axle bar */}
          <rect x="28" y="65" width="24" height="3" rx="1" fill="#334155" />

          {/* ── Front wheel ── */}
          <g>
            <animateTransform attributeName="transform" type="rotate"
              from="0 162 68" to="360 162 68" dur="0.9s" repeatCount="indefinite" />
            <circle cx="162" cy="68" r="12" fill="#1e293b" />
            <circle cx="162" cy="68" r="8"  fill="#334155" />
            <circle cx="162" cy="68" r="3"  fill="#64748b" />
            <line x1="162" y1="59" x2="162" y2="77" stroke="#475569" strokeWidth="1.3" />
            <line x1="153" y1="68" x2="171" y2="68" stroke="#475569" strokeWidth="1.3" />
            <line x1="155.6" y1="61.6" x2="168.4" y2="74.4" stroke="#475569" strokeWidth="1" />
            <line x1="168.4" y1="61.6" x2="155.6" y2="74.4" stroke="#475569" strokeWidth="1" />
          </g>

          {/* ── Road surface ── */}
          <rect x="0" y="79" width="200" height="1" fill="#cbd5e1" />

          {/* ── Moving road dashes (seamless loop every 40px) ── */}
          <g>
            <animateTransform attributeName="transform" type="translate"
              from="0,0" to="-40,0" dur="0.38s" repeatCount="indefinite" />
            {[-8, 32, 72, 112, 152, 192, 232].map((x) => (
              <rect key={x} x={x} y="77" width="24" height="2" rx="1" fill="#94a3b8" opacity="0.45" />
            ))}
          </g>
        </svg>
      </div>

      <p
        className="text-sm font-medium text-muted-foreground tracking-[0.2em] uppercase animate-pulse"
        role="status"
        aria-live="polite"
      >
        Cargando…
      </p>
    </div>
  )
}
